import { createElement } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';

/**
 * 🔴 THE LAYOUT PRIMITIVES — design-system plan DS-9a (scope §1.1). Screens stop hand-writing flex and margins: a
 * page is a `Stack` of a `PageHeader`, `Toolbar`s, `Section`s and `Card`s, and a row of buttons is a `Cluster`. Each
 * primitive renders the classes the stylesheets already have wherever one exists (`screen__head`, `doc-form`,
 * `doc-meta`, `dash-card` …), and a new class only where nothing did. Every one passes `className` and its element's
 * native attributes through.
 */

/** The space steps a gap may take: the space tokens, `--space-2` (4px) to `--space-16` (32px). */
export type Space = 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16;

/** The classes of a primitive: its base, its modifiers when they apply, then the caller's. */
export function classes(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(' ');
}

/**
 * Children one above the other, `gap` apart (default 10: 20px, the rhythm between the blocks of a screen). Inside a
 * Stack the old blocks' own margins (a header's, a toolbar's, a form grid's …) are cancelled by the stylesheet, so the
 * gap is the only space between them, and a `Section` stands 8px further off, as a section heading always did.
 */
export function Stack({
  as = 'div',
  gap = 10,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLElement> & {
  as?: 'div' | 'section' | 'form' | 'ul' | 'ol';
  gap?: Space;
  noValidate?: boolean;
  children?: ReactNode;
}) {
  return createElement(as, { ...props, className: classes('wb-stack', gap !== 10 && `wb-stack--g${gap}`, className) }, children);
}
